import React from 'react';
import { Plane, Zap, Snowflake, FileText, Package } from 'lucide-react';
import DirectServiceTemplate from '../components/DirectServiceTemplate';

const accent = {
  heroBg: 'bg-[radial-gradient(circle_at_top,rgba(168,85,247,0.14),transparent_70%)]',
  pill: 'bg-purple-50 text-purple-700 border-purple-100',
  cta: 'bg-[#7c3aed] hover:bg-[#6d28d9] shadow-purple-900/15',
  kpiBox: 'bg-purple-50 border-purple-100 text-purple-700',
  polyline: '#7c3aed',
  inputFocus: 'focus:border-purple-400',
  orderCta: 'bg-purple-600 hover:bg-purple-700 text-white',
  packageActive: 'border-purple-200 bg-purple-50/40',
  toggleOn: 'border-purple-200 bg-purple-50/40',
  switchTrack: 'bg-purple-600 border-purple-600',
  switchKnobOn: 'bg-white translate-x-0',
  stepBox: 'bg-purple-50 border-purple-100 text-purple-700',
  modalHeader: 'bg-gradient-to-l from-purple-700 to-fuchsia-600',
};

const meta = {
  Icon: Plane,
  badge: 'خدمات مستقیم — هوایی (کارگو)',
  title: 'هوایی (کارگو)',
  subtitle:
    'برای محموله‌های حساس به زمان (دارو/قطعات فوری/نمونه‌ها): صدور AWB، هماهنگی فرودگاهی، کنترل زنجیره سرد (در صورت نیاز)، و اتصال به توزیع شهری/بین‌شهری.',
  quickTitle: 'سریع‌ترین سناریو با کنترل SLA',
  quickDesc: 'زمان کمتر، با تمرکز بر کنترل اسناد، زمان‌بندی پرواز و تحویل فرودگاهی.',
  sla: '۲ تا ۱۲ ساعت',
  baseDays: 2,
  baseCost: 180,
  placeholders: {
    origin: 'مثلاً: فرودگاه IST (استانبول)',
    destination: 'مثلاً: فرودگاه امام خمینی',
    cargo: 'مثلاً: دارو (کنترل دما) / قطعات فوری',
  },
};

const packages = [
  {
    key: 'standard',
    title: 'استاندارد',
    desc: 'تعادل زمان/هزینه برای کارگوهای عمومی با کنترل اسناد.',
    badge: 'پیشنهادی',
    badgeCls: 'bg-purple-50 text-purple-700 border-purple-100',
    baseDays: 2,
    baseCost: 180,
  },
  {
    key: 'priority',
    title: 'اولویت‌دار',
    desc: 'اولویت رزرو، کنترل سریع‌تر و هماهنگی نزدیک‌تر در فرودگاه.',
    badge: 'سریع‌تر',
    badgeCls: 'bg-amber-50 text-amber-700 border-amber-100',
    baseDays: 1,
    baseCost: 240,
  },
  {
    key: 'cold-chain',
    title: 'زنجیره سرد',
    desc: 'مناسب دارو/مواد حساس با کنترل دما و تحویل کنترل‌شده.',
    badge: 'حساس',
    badgeCls: 'bg-blue-50 text-blue-700 border-blue-100',
    baseDays: 2,
    baseCost: 260,
  },
];

const capabilities = [
  {
    title: 'مدیریت پرواز و SLA',
    desc: 'هماهنگی برنامه پرواز، رزرو فضا، و تحویل سریع در مقصد.',
    items: ['رزرو/ظرفیت', 'کنترل Cut-off', 'ETA', 'اعلان تغییرات'],
  },
  {
    title: 'اسناد و انطباق',
    desc: 'کنترل AWB و مدارک برای جلوگیری از توقف فرودگاهی/ترخیص.',
    items: ['AWB', 'Invoice', 'Packing List', 'مجوزهای کالا'],
  },
  {
    title: 'کالاهای حساس',
    desc: 'پشتیبانی از محموله‌های حساس به دما/زمان با فرآیند کنترل‌شده.',
    items: ['Cold chain', 'کنترل دما', 'تحویل کنترل‌شده', 'ثبت گزارش'],
  },
];

const documents = [
  {
    title: 'AWB (بارنامه هوایی)',
    desc: 'سند اصلی حمل هوایی شامل فرستنده/گیرنده، فرودگاه‌ها و مشخصات بار.',
    state: 'الزامی',
    stateCls: 'bg-white text-gray-700 border-gray-100',
    tags: ['AWB', 'اسناد حمل', 'هوایی'],
  },
  {
    title: 'Packing List',
    desc: 'برای تطبیق در پذیرش فرودگاهی و کنترل مغایرت.',
    state: 'الزامی',
    stateCls: 'bg-white text-gray-700 border-gray-100',
    tags: ['اسناد پایه', 'کنترل'],
  },
  {
    title: 'مجوزهای کالا (در صورت نیاز)',
    desc: 'بهداشت/قرنطینه/استاندارد یا مجوزهای خاص براساس HS.',
    state: 'وابسته به کالا',
    stateCls: 'bg-amber-50 text-amber-700 border-amber-100',
    tags: ['مجوز', 'HS', 'کنترل ریسک'],
  },
  {
    title: 'گواهی دما (برای زنجیره سرد)',
    desc: 'برای کالاهای حساس به دما؛ ثبت و گزارش شرایط نگهداری.',
    state: 'در صورت نیاز',
    stateCls: 'bg-blue-50 text-blue-700 border-blue-100',
    tags: ['Cold chain', 'کنترل دما'],
  },
];

const processSteps = [
  { title: 'ثبت RFQ / رزرو', desc: 'مبدا/مقصد، نوع کالا، وزن/حجم و نیازهای ویژه را اعلام کنید.' },
  { title: 'کنترل Cut-off و پذیرش', desc: 'زمان پذیرش بار و محدودیت‌های پرواز بررسی می‌شود.' },
  { title: 'صدور AWB', desc: 'بارنامه هوایی صادر و برای مراحل بعدی ثبت می‌گردد.' },
  { title: 'پرواز و ورود', desc: 'وضعیت پرواز و ورود به مقصد رهگیری می‌شود.' },
  { title: 'تحویل فرودگاهی و توزیع', desc: 'تحویل در مقصد و در صورت نیاز اتصال به حمل جاده‌ای/توزیع شهری.' },
];

const providers = [
  {
    name: 'اپراتور کارگو — رزرو و AWB',
    Icon: Zap,
    badges: ['AWB', 'رزرو سریع', 'SLA'],
    note: 'مناسب برای کارگوهای عمومی و حساس به زمان با کنترل دقیق زمان‌بندی.',
    score: 4.6,
    scoreCls: 'bg-purple-50 text-purple-700 border-purple-100',
    response: 'کمتر از ۳ ساعت',
    coverage: 'بین‌المللی/داخلی',
    capacity: 'بالا',
  },
  {
    name: 'زنجیره سرد (Cold chain)',
    Icon: Snowflake,
    badges: ['کنترل دما', 'ثبت گزارش', 'تحویل کنترل‌شده'],
    note: 'برای دارو و کالاهای حساس به دما با کنترل شرایط نگهداری.',
    score: 4.4,
    scoreCls: 'bg-blue-50 text-blue-700 border-blue-100',
    response: '۳ تا ۶ ساعت',
    coverage: 'فرودگاه/شهری',
    capacity: 'متوسط',
  },
  {
    name: 'کنترل اسناد و ترخیص فرودگاهی',
    Icon: FileText,
    badges: ['چک‌لیست', 'کاهش توقف', 'کنترل مغایرت'],
    note: 'کنترل مدارک برای جلوگیری از توقف در مراحل پذیرش/تحویل فرودگاهی.',
    score: 4.2,
    scoreCls: 'bg-amber-50 text-amber-700 border-amber-100',
    response: 'کمتر از ۱ روز',
    coverage: 'ملی',
    capacity: 'قابل افزایش',
  },
  {
    name: 'تحویل آخر (جاده‌ای/شهری)',
    Icon: Package,
    badges: ['Last mile', 'تحویل فوری', 'درب-به-درب'],
    note: 'برای تحویل نهایی از فرودگاه به مقصد با SLA.',
    score: 4.1,
    scoreCls: 'bg-gray-50 text-gray-700 border-gray-100',
    response: '۱ تا ۴ ساعت',
    coverage: 'تهران/شهرهای اصلی',
    capacity: 'متوسط',
  },
];

const hubs = [
  { name: 'فرودگاه IST (استانبول)', lat: 41.275, lng: 28.752, note: 'مبدا نمونه' },
  { name: 'فرودگاه امام خمینی', lat: 35.416, lng: 51.152, note: 'تحویل فرودگاهی' },
  { name: 'تهران (انبار سرد/توزیع)', lat: 35.72, lng: 51.4, note: 'اتصال به توزیع' },
  { name: 'مشهد (نمونه مقصد داخلی)', lat: 36.26, lng: 59.62, note: 'توزیع داخلی' },
];

const faqs = [
  { q: 'چه زمانی حمل هوایی توصیه می‌شود؟', a: 'برای کالاهای حساس به زمان یا ارزش بالا که تأخیر هزینه‌زا است.' },
  { q: 'مهم‌ترین سند حمل هوایی چیست؟', a: 'AWB (بارنامه هوایی) که اطلاعات اصلی حمل را ثبت می‌کند.' },
  { q: 'برای زنجیره سرد چه چیزی لازم است؟', a: 'اعلام شرایط دمایی، بسته‌بندی مناسب، و در صورت نیاز گواهی/گزارش دما.' },
  { q: 'چطور تحویل نهایی انجام می‌شود؟', a: 'پس از تحویل فرودگاهی، با حمل جاده‌ای/شهری به مقصد نهایی متصل می‌شود.' },
];

export default function DirectAirCargo() {
  return (
    <DirectServiceTemplate
      meta={meta}
      accent={accent}
      formDefaults={{
        origin: 'فرودگاه IST (استانبول)',
        destination: 'فرودگاه امام خمینی',
        cargo: 'دارو (کنترل دما)',
        incoterm: 'DAP',
        weightKg: 920,
        volumeM3: 5.2,
        isExpress: true,
        needsInsurance: true,
        isSpecialHandling: true,
      }}
      packages={packages}
      capabilities={capabilities}
      documents={documents}
      processSteps={processSteps}
      providers={providers}
      hubs={hubs}
      faqs={faqs}
    />
  );
}

